import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, EyeOff, Loader2 } from 'lucide-react';
import axiosClient from '../../api/axiosClient';

export default function ClientLoginModal({ onClose, onSwitchToSignup }) {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await axiosClient.post('/trials/login', { email, token });
      localStorage.setItem('trialToken', res.data.token);
      // Force reload to stay on same page and update auth state
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password/token.');
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }} 
          animate={{ opacity: 1, scale: 1, y: 0 }} 
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center p-6 border-b border-slate-100">
            <h3 className="text-xl font-display font-bold text-slate-900">Log In</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X size={24} />
            </button>
          </div>
          
          <form onSubmit={handleLogin} className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
              <input required type="email" className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow" 
                value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@school.edu" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <div className="relative">
                <input required type={showPassword ? "text" : "password"} className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow pr-10" 
                  value={token} onChange={e => setToken(e.target.value)} placeholder="Enter your password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <button disabled={loading} type="submit" className="w-full bg-primary-600 text-white font-medium py-3 rounded-lg hover:bg-primary-700 transition-colors mt-6 disabled:opacity-70 flex justify-center items-center gap-2">
              {loading ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Authenticating...</span> : 'Log In'}
            </button>

            <div className="mt-6 flex flex-col gap-3">
              <div className="text-center text-sm text-slate-500">
                Don't have an account yet?{' '}
                <button type="button" onClick={onSwitchToSignup} className="text-primary-600 font-semibold hover:text-primary-700 hover:underline">
                  Sign up
                </button>
              </div>
              <div className="relative flex py-1 items-center">
                  <div className="flex-grow border-t border-slate-200"></div>
                  <span className="flex-shrink-0 mx-4 text-slate-400 text-xs uppercase tracking-wider">or</span>
                  <div className="flex-grow border-t border-slate-200"></div>
              </div>
              <Link 
                to="/admin" 
                onClick={onClose}
                className="w-full border border-slate-200 bg-slate-50 text-slate-700 font-medium py-2.5 rounded-lg hover:bg-slate-100 transition-colors text-center text-sm"
              >
                Go to Admin Portal
              </Link>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
